<template>
  <div>
    <div v-if="loading" class="dw-loading">
      <b-spinner small></b-spinner> Loading...
    </div>
    <div v-else-if="!scorecard || scorecard.compiled_score == null" class="dw-empty">
      No fleet driver data available for this month yet.
    </div>
    <template v-else>
      <div class="dw-compiled-score-badge" :class="scoreClass">
        <span class="dw-score-number">{{ scorecard.compiled_score }}</span>
        <span class="dw-score-label">/ 100 fleet average &middot; {{ scoreTier }}</span>
      </div>
      <!-- mirrors the "Risk Metrics" / "Idling & Fuel Efficiency" metric-card
           rows on the full Driver Manager dashboard, just the headline
           figures rather than the full multi-section breakdown. -->
      <div class="dw-stat-grid" style="margin-top: 12px">
        <div v-if="scorecard.risk_score != null" class="dw-stat-tile">
          <div class="dw-stat-tile-label">Safety Score</div>
          <div class="dw-stat-tile-value" :class="fieldClass('risk_score', scorecard.risk_score)">{{ scorecard.risk_score }}</div>
        </div>
        <div v-if="scorecard.idling_percentage != null" class="dw-stat-tile">
          <div class="dw-stat-tile-label">Idling %</div>
          <div class="dw-stat-tile-value" :class="fieldClass('idling_percentage', scorecard.idling_percentage)">{{ formatNumber(scorecard.idling_percentage) }}%</div>
        </div>
        <div v-if="scorecard.miles_per_gallon != null" class="dw-stat-tile">
          <div class="dw-stat-tile-label">MPG</div>
          <div class="dw-stat-tile-value" :class="fieldClass('miles_per_gallon', scorecard.miles_per_gallon)">{{ formatNumber(scorecard.miles_per_gallon) }}</div>
        </div>
        <div v-if="scorecard.dvir_completion_rate != null" class="dw-stat-tile">
          <div class="dw-stat-tile-label">DVIR Completion</div>
          <div class="dw-stat-tile-value" :class="fieldClass('dvir_completion_rate', scorecard.dvir_completion_rate)">{{ formatNumber(scorecard.dvir_completion_rate, 1) }}%</div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import moment from 'moment'
import { cm_get_fleet_scorecard } from '../../networking/apis'
import { get_fleet_scorecard } from '../../networking/tbn_apis'
import { mergeThresholds, getCompiledScoreClass, getFieldClass } from '../../utils/driver_manager_thresholds'

export default {
  name: 'widget_driver_manager',
  props: {
    tenant: { type: String, required: true },
    isCmTenant: { type: Boolean, default: true },
  },
  data() {
    return {
      loading: true,
      scorecard: null,
      thresholds: null,
    }
  },
  computed: {
    mergedThresholds() {
      return mergeThresholds(this.thresholds)
    },
    // getCompiledScoreClass returns e.g. 'compiled-score-elite', now defined
    // once in the shared family (assets/css/driver-manager-mobile.css) -- the
    // old dw- prefixed copy of the tier tints is gone.
    scoreClass() {
      const cls = getCompiledScoreClass(Number(this.scorecard.compiled_score), this.mergedThresholds.compiled_score, this.scoreTier)
      return cls || 'compiled-score-solid'
    },
    // The backend is the source of truth: it's the only place with access to
    // Insufficient Data. Only fall back to recomputing from a raw
    // elite/solid/coaching cfg if the backend didn't supply a tier at all.
    scoreTier() {
      if (this.scorecard.compiled_score_tier) return this.scorecard.compiled_score_tier
      const cfg = this.mergedThresholds.compiled_score
      const s = Number(this.scorecard.compiled_score)
      if (cfg) {
        if (s >= Number(cfg.elite)) return 'Elite'
        if (s >= Number(cfg.solid)) return 'Solid'
        if (s >= Number(cfg.coaching)) return 'Fair'
        return 'High Risk'
      }
      return ''
    },
  },
  created() {
    this.load()
  },
  methods: {
    fieldClass(fieldName, value) {
      return getFieldClass(fieldName, value, this.mergedThresholds)
    },
    formatNumber(value, precision = 2) {
      if (value === null || value === undefined) return '—'
      const n = Number(value)
      return isFinite(n) ? n.toFixed(precision) : value
    },
    async load() {
      this.loading = true
      try {
        const month = moment().format('YYYY-MM')
        const fetch = this.isCmTenant
          ? cm_get_fleet_scorecard(this.tenant, month, month)
          : get_fleet_scorecard(this.tenant, month, month)
        const response = await fetch
        const responseData = (response && response.value && response.value.data) || {}
        this.scorecard = responseData.error ? null : (responseData.results || null)
        this.thresholds = responseData.scorecard_thresholds || null
      } catch (e) {
        this.scorecard = null
      }
      this.loading = false
    },
  },
}
</script>
